import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useToast } from '@/components/ui/Toast';
import { Authentticatevalues, ROUTES, STORAGE_KEYS } from '@/constants';
import { taskcontants, TaskSectionType } from '@/constants/taskConstants';
import { useLogoutMutation } from '@/hooks/auth';
import { usePreventBackNavigation } from '@/hooks/usePreventBackNavigation';
import { usePreventPullToRefresh } from '@/hooks/usePreventPullToRefresh';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { UpdateUserLogAsync } from '@/service/AuthenticationService';
import { GetAllTaskToRelease, GetTaskListAsync, UpdateTaskAsync } from '@/service/ManhattanService';
import { updateUserLogsWhenInternetConnectivityChanged } from '@/service/userLogService';
import {
  store,
  useAppSelector,
  selectUser,
  selectPlant,
  selectSessionId,
  selectConfig,
} from '@/store';

import type { UserLogRequestModel } from '@/service/types/authenticatetypes';

function formatBreakTime(totalSeconds: number) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return {
    hours,
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0'),
    hoursStr: String(hours).padStart(2, '0'),
  };
}

function resolveBreakStart(): { startTime: number; resuming: boolean } {
  const saved = localStorage.getItem(STORAGE_KEYS.breakTimerStart);
  if (saved) {
    const parsed = parseInt(saved, 10);
    if (!Number.isNaN(parsed) && parsed > 0) {
      return { startTime: parsed, resuming: true };
    }
  }
  return { startTime: Date.now(), resuming: false };
}

export function BreakModePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const logoutMutation = useLogoutMutation();

  useTokenRefresh();

  // Read auth context for user log API calls
  const user = useAppSelector(selectUser);
  const plant = useAppSelector(selectPlant);
  const sessionId = useAppSelector(selectSessionId);
  const config = useAppSelector(selectConfig);
  const maxBreakDurationSeconds = parseFloat(config?.settings.APP_AUTO_LOGOUT_TIME ?? '0');

  const { startTime: resolvedStart, resuming: isResumingFromPersisted } = resolveBreakStart();
  const initialElapsed = isResumingFromPersisted
    ? Math.max(0, Math.floor((Date.now() - resolvedStart) / 1000))
    : 0;

  // Local timer state — NOT stored in Redux
  const [elapsedSeconds, setElapsedSeconds] = useState(initialElapsed);
  const [isResuming, setIsResuming] = useState(false);
  const startedAtRef = useRef<number>(resolvedStart);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resumeInFlightRef = useRef(false);
  const breakLogFiredRef = useRef(false);
  const autoLogoutFiredRef = useRef(false);

  // Determine if break originated from shuttle transfer (passed via query param)
  const fromShuttleTransfer = searchParams.get('from') === 'shuttle';

  // Mark break mode active so the global BreakModeGuard can intercept
  // navigation away (e.g. direct URL paste in the address bar).
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.breakModeActive, 'true');
    localStorage.setItem(STORAGE_KEYS.breakModeFrom, fromShuttleTransfer ? 'shuttle' : '');
  }, [fromShuttleTransfer]);

  // Persist the break start timestamp (only on fresh entry)
  useEffect(() => {
    if (!isResumingFromPersisted) {
      localStorage.setItem(STORAGE_KEYS.breakTimerStart, String(startedAtRef.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Log break start on mount — only on fresh entry, not when resuming after app relaunch
  useEffect(() => {
    if (isResumingFromPersisted) return;
    if (breakLogFiredRef.current) return;
    breakLogFiredRef.current = true;

    void UpdateUserLogAsync({
      user: user?._id ?? '',
      status: Authentticatevalues.UserStatusBreak,
      activity_date_time: new Date().toISOString(),
      session_id: sessionId ?? '',
      plant_id: plant?._id,
    }).catch(() => {
      // Fire-and-forget — don't block the UI if logging fails
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  usePreventPullToRefresh();

  // Start timer — startedAtRef and elapsedSeconds are already initialized
  // with the resume offset during component construction.
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Re-sync timer when app resumes from background (frozen/discarded state).
  // setInterval stops running while the page is frozen — recalculate from stored start time.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Connectivity-aware logging — Matches Xamarin connectivity subscription
  useEffect(() => {
    const orgId = String(plant?.org_id ?? '');
    const userId = user?._id ?? '';

    const handleOnline = () => {
      showToast(t('connectivity.online', 'Internet connection restored'), { type: 'success' });
      void updateUserLogsWhenInternetConnectivityChanged({
        OrgId: orgId,
        UserId: userId,
        NetworkAccess: 'Internet',
        ActionDatetime: new Date().toISOString(),
      });
    };

    const handleOffline = () => {
      showToast(t('connectivity.offline', 'You are offline'), { type: 'warning' });
      void updateUserLogsWhenInternetConnectivityChanged({
        OrgId: orgId,
        UserId: userId,
        NetworkAccess: 'None',
        ActionDatetime: new Date().toISOString(),
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [plant?.org_id, user?._id, showToast, t]);

  const { hours, minutes, seconds, hoursStr } = formatBreakTime(elapsedSeconds);

  const handleResumeWork = useCallback(() => {
    if (resumeInFlightRef.current) return;
    resumeInFlightRef.current = true;

    if (intervalRef.current) clearInterval(intervalRef.current);
    setIsResuming(true);

    // Fire-and-forget — don't await, navigate immediately
    void UpdateUserLogAsync({
      user: user?._id ?? '',
      status: Authentticatevalues.UserStatusContinue,
      activity_date_time: new Date().toISOString(),
      session_id: sessionId ?? '',
      plant_id: plant?._id,
    }).catch(() => undefined);

    if (!fromShuttleTransfer) {
      void UpdateUserLogAsync({
        user: user?._id ?? '',
        status: Authentticatevalues.UserStatusStartYardWork,
        activity_date_time: new Date().toISOString(),
        session_id: sessionId ?? '',
        plant_id: plant?._id,
      }).catch(() => undefined);
    }

    localStorage.removeItem(STORAGE_KEYS.breakModeActive);
    localStorage.removeItem(STORAGE_KEYS.breakModeFrom);
    localStorage.removeItem(STORAGE_KEYS.breakTimerStart);

    const target = fromShuttleTransfer ? ROUTES.shuttleTransfer : ROUTES.dashboard;
    navigate(target, { replace: true });
  }, [user?._id, sessionId, plant?._id, fromShuttleTransfer, navigate]);

  // Auto-logout when break exceeds configured duration
  useEffect(() => {
    if (autoLogoutFiredRef.current) return;
    if (elapsedSeconds >= maxBreakDurationSeconds) {
      autoLogoutFiredRef.current = true;
      void handleLogout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsedSeconds, maxBreakDurationSeconds]);

  const handleLogout = async () => {
    try {
      const [myTaskListRes, inProgressListRes] = await Promise.all([
        GetTaskListAsync(TaskSectionType.MyTaskList).catch(() => null),
        GetTaskListAsync(TaskSectionType.OnGoingTask).catch(() => null),
      ]);

      const acceptedTasks = myTaskListRes?.Payload.Body.Task.TaskDetails ?? [];
      const inProgressTasks = inProgressListRes?.Payload.Body.Task.TaskDetails ?? [];

      if (acceptedTasks.length > 0 || inProgressTasks.length > 0) {
        await ReleaseAllTasks();
      } else {
        await OnSuccessfulTaskRelease();
      }
    } catch {
      // Logout error handled by mutation callbacks below
    }

    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        navigate(ROUTES.login, { replace: true });
      },
      onError: () => {
        Object.values(STORAGE_KEYS).forEach((key) => {
          localStorage.removeItem(key);
        });
        navigate(ROUTES.login, { replace: true });
      },
    });
  };

  const ReleaseAllTasks = async () => {
    try {
      const updateTaskRequest = await GetAllTaskToRelease();
      const cancelTaskResponse = await UpdateTaskAsync(updateTaskRequest);

      if (
        cancelTaskResponse.payload.Body.Status == taskcontants.SuccessStatus &&
        cancelTaskResponse.payload.Body.UpdateTaskDetails[0]?.Status == taskcontants.SuccessStatus
      ) {
        await OnSuccessfulTaskRelease();
      } else {
        if (cancelTaskResponse.payload.Body.UpdateTaskDetails.length > 0) {
          const failedTasks = cancelTaskResponse.payload.Body.UpdateTaskDetails.filter(
            (x) => x.Status != taskcontants.SuccessStatus
          );
          for (const task of failedTasks) {
            if (
              task.Status != taskcontants.SuccessStatus &&
              task.StatusMessage.includes(task.TaskId) &&
              (task.StatusMessage.toLowerCase().includes(
                taskcontants.CompleteTaskStatus.toLowerCase()
              ) ||
                task.StatusMessage.toLowerCase().includes(taskcontants.CancelledTaskStatus))
            ) {
              await OnSuccessfulTaskRelease();
              return;
            } else {
              showToast(task.StatusMessage, { type: 'warning' });
              return;
            }
          }
        }
      }
    } catch {
      // Release tasks error — continue with logout flow
    }
  };

  const OnSuccessfulTaskRelease = async () => {
    try {
      const state = store.getState();
      await LogUserLogout();
      localStorage.clear();
      const deviceId = state.global.config?.device._id ?? '';
      localStorage.setItem('device_id', deviceId);
      navigate(ROUTES.login);
    } catch {
      // Task release cleanup error
    }
  };

  const LogUserLogout = async () => {
    const userId = store.getState().user.user?._id ?? '';
    const plantId = store.getState().user.plant?._id ?? '';
    const sid = store.getState().user.sessionId ?? '';

    const userLogs: UserLogRequestModel = {
      user: userId,
      plant_id: plantId,
      status: taskcontants.UserStatusEndYardWork,
      activity_date_time: new Date().toISOString(),
      session_id: sid,
    };
    await UpdateUserLogAsync(userLogs);

    const userLog: UserLogRequestModel = {
      user: userId,
      plant_id: plantId,
      status: Authentticatevalues.UserStatusAutoLogout,
      activity_date_time: new Date().toISOString(),
      session_id: sid,
    };
    await UpdateUserLogAsync(userLog);
  };

  // Silently absorb back button — PWA cannot minimize, so back does nothing.
  // User must use the "Resume Work" button to leave break mode.
  usePreventBackNavigation(true, resumeInFlightRef);

  return (
    <div className="flex flex-col min-h-screen bg-white px-4 sm:px-16 py-24 justify-between">
      {/* Row 0: Header — icon, title, description */}
      <div className="flex flex-col items-center justify-center gap-20">
        <div className="flex flex-col items-center justify-center gap-2">
          <img src="/images/breakmode_icon.svg" alt="" aria-hidden="true" className="h-16 w-16" />
          <span className="font-poppins font-normal text-black text-2xl sm:text-4xl text-center">
            {t('breakMode.title', 'You are on Break')}
          </span>
          <span className="text-lg text-[#425b6e] font-normal">
            {t('breakMode.subtitle', 'Tap on resume work to continue where you left off.')}
          </span>
        </div>

        {/* Row 1: Illustration */}
        <div className="flex-col flex items-center justify-center">
          <img
            src="/images/break_illustration.svg"
            alt=""
            aria-hidden="true"
            className="h-[350px] w-[350px]"
          />

          {/* Row 2: Timer + Resume */}
          <div className="flex px-14 py-5 border-[0.9px] border-[#425b6e] rounded-xl gap-1">
            {hours > 0 && (
              <>
                <span className="font-poppins text-4xl font-normal text-[#003366]">{hoursStr}</span>
                <span className="font-poppins text-4xl font-normal text-[#003366]">
                  {t('timer.h')}
                </span>
                <span className="font-poppins text-4xl font-normal text-[#003366] mx-1">:</span>
              </>
            )}
            <span className="font-poppins text-4xl font-normal text-[#003366]">{minutes}</span>
            <span className="font-poppins text-4xl font-normal text-[#003366]">{t('timer.m')}</span>
            <span className="font-poppins text-4xl font-normal text-[#003366] mx-1">:</span>
            <span className="font-poppins text-4xl font-normal text-[#003366]">{seconds}</span>
            <span className="font-poppins text-4xl font-normal text-[#003366]">{t('timer.s')}</span>
          </div>

          <div className="flex items-center justify-center pt-14">
            <button
              onClick={handleResumeWork}
              disabled={isResuming}
              className="w-full flex items-center justify-center py-2 px-14 rounded-lg bg-[#003366] hover:bg-[#e6e6e6] hover:text-[#424242] hover:font-medium text-white font-poppins text-[13px] font-normal disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('breakMode.resumeWork', 'Resume Work')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
